import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Search, Edit2, Trash2, QrCode, ArrowUpRight, ArrowDownRight, Filter, X, Printer, Wrench, Ban, Sparkles } from 'lucide-react';
import AIDetectDialog from '../components/AIDetectDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  { value: 'checked_out', label: 'Checked Out', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  { value: 'under_maintenance', label: 'Under Maintenance', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20' },
  { value: 'unavailable', label: 'Unavailable', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
];

const Items = () => {
  const { getAuthHeaders, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  // Selection for batch printing
  const [selectedItems, setSelectedItems] = useState(new Set());
  
  // Dialogs
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showBatchPrintDialog, setShowBatchPrintDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAIDetect, setShowAIDetect] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    item_number: '',
    length: '',
    quantity: 1,
    storage_location_id: '',
    notes: '',
    status: 'available'
  });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory && filterCategory !== 'all') params.append('category_id', filterCategory);
      if (filterLocation && filterLocation !== 'all') params.append('storage_location_id', filterLocation);
      if (filterStatus && filterStatus !== 'all') params.append('item_status', filterStatus);

      const [itemsRes, categoriesRes, locationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/items?${params}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/categories`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/storage-locations`, { headers: getAuthHeaders() })
      ]);
      
      setItems(itemsRes.data);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, search, filterCategory, filterLocation, filterStatus]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const handleAIDetected = (detected) => {
    const matchedCategory = categories.find(c => 
      c.name.toLowerCase() === (detected.category_suggestion || '').toLowerCase()
    );
    
    setSelectedItem(null);
    setFormData({
      name: detected.name || '',
      category_id: matchedCategory?.id || '',
      item_number: '',
      length: '',
      quantity: 1,
      storage_location_id: '',
      notes: detected.description || '',
      status: 'available'
    });
    setShowItemDialog(true);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setFormData({
        name: item.name,
        category_id: item.category_id,
        item_number: item.item_number,
        length: item.length || '',
        quantity: item.quantity,
        storage_location_id: item.storage_location_id,
        notes: item.notes || '',
        status: item.status || 'available'
      });
    } else {
      setSelectedItem(null);
      setFormData({
        name: '',
        category_id: '',
        item_number: '',
        length: '',
        quantity: 1,
        storage_location_id: '',
        notes: '',
        status: 'available'
      });
    }
    setShowItemDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selectedItem) {
        await axios.put(`${API_URL}/api/items/${selectedItem.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API_URL}/api/items`, formData, { headers: getAuthHeaders() });
        toast.success('Item created successfully');
      }
      setShowItemDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/items/${selectedItem.id}`, { headers: getAuthHeaders() });
      toast.success('Item deleted successfully');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const handleCheckout = async (item) => {
    try {
      await axios.post(`${API_URL}/api/items/${item.id}/checkout`, {}, { headers: getAuthHeaders() });
      toast.success(`${item.name} checked out`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out item');
    }
  };

  const handleCheckin = async (item) => {
    try {
      await axios.post(`${API_URL}/api/items/${item.id}/checkin`, {}, { headers: getAuthHeaders() });
      toast.success(`${item.name} checked in`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in item');
    }
  };

  const handlePrintQR = (item) => {
    const printWindow = window.open('', '_blank');
    const qrUrl = `${window.location.origin}/item/${item.id}`;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Code - ${item.name}</title>
          <style>
            body { font-family: Arial, sans-serif; text-align: center; padding: 20px; }
            .qr-container { display: inline-block; padding: 20px; border: 2px solid #000; margin: 10px; }
            .item-name { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
            .item-number { font-size: 14px; color: #666; margin-bottom: 15px; }
            .qr-code { margin: 15px 0; }
            .scan-text { font-size: 12px; color: #888; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="item-name">${item.name}</div>
            <div class="item-number">${item.item_number}</div>
            <div class="qr-code">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
            </div>
            <div class="scan-text">Scan to view item details</div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleBatchPrint = () => {
    const selectedItemsList = items.filter(item => selectedItems.has(item.id));
    if (selectedItemsList.length === 0) {
      toast.error('Please select items to print');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    const qrCodes = selectedItemsList.map(item => {
      const qrUrl = `${window.location.origin}/item/${item.id}`;
      return `
        <div class="qr-container">
          <div class="item-name">${item.name}</div>
          <div class="item-number">${item.item_number}</div>
          <div class="qr-code">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}" alt="QR Code" />
          </div>
          <div class="scan-text">Scan for details</div>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Codes - Batch Print (${selectedItemsList.length} items)</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 10px; }
            .print-grid { display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-start; }
            .qr-container { 
              display: inline-block; 
              padding: 15px; 
              border: 1px solid #000; 
              text-align: center;
              width: 180px;
              page-break-inside: avoid;
            }
            .item-name { font-size: 12px; font-weight: bold; margin-bottom: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .item-number { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 10px; }
            .qr-code { margin: 10px 0; }
            .qr-code img { width: 120px; height: 120px; }
            .scan-text { font-size: 10px; color: #666; }
            @media print { 
              body { padding: 0; margin: 0; }
              .qr-container { border: 1px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="print-grid">
            ${qrCodes}
          </div>
          <script>
            window.onload = function() { 
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setSelectedItems(new Set());
    setShowBatchPrintDialog(false);
  };

  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterLocation('');
    setFilterStatus('');
  };

  const hasFilters = filterCategory || filterLocation || filterStatus;

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <Badge variant="secondary" className={statusConfig.color}>
        {status === 'under_maintenance' && <Wrench className="w-3 h-3 mr-1" />}
        {status === 'unavailable' && <Ban className="w-3 h-3 mr-1" />}
        {statusConfig.label}
      </Badge>
    );
  };

  const canCheckout = (item) => {
    return !item.checked_out && item.status !== 'under_maintenance' && item.status !== 'unavailable';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="items-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Inventory Items</h1>
          <p className="text-muted-foreground mt-1">Manage your AV equipment inventory</p>
        </div>
        <div className="flex gap-2">
          {selectedItems.size > 0 && (
            <Button 
              variant="outline" 
              data-testid="batch-print-btn" 
              onClick={() => setShowBatchPrintDialog(true)}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print QR ({selectedItems.size})
            </Button>
          )}
          {isManager() && (
            <>
              <Button variant="outline" data-testid="ai-detect-item-btn" onClick={() => setShowAIDetect(true)}>
                <Sparkles className="w-4 h-4 mr-2" />
                AI Detect
              </Button>
              <Button data-testid="add-item-btn" onClick={() => handleOpenDialog()}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-border/50">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                placeholder="Search by name, item number, or notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger data-testid="filter-category" className="w-[160px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger data-testid="filter-location" className="w-[160px]">
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status" className="w-[160px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">
            Equipment ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={items.length > 0 && selectedItems.size === items.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Item #</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-center">Qty</TableHead>
                  <TableHead>Length</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      No items found. {isManager() && 'Add your first item to get started.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id} className="table-row-hover" data-testid={`item-row-${item.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedItems.has(item.id)}
                          onCheckedChange={() => toggleSelectItem(item.id)}
                          data-testid={`select-item-${item.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                          {item.item_number}
                        </code>
                      </TableCell>
                      <TableCell>{item.category_name}</TableCell>
                      <TableCell>{item.storage_location_name}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell>{item.length || '-'}</TableCell>
                      <TableCell>{getStatusBadge(item.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`qr-btn-${item.id}`}
                            onClick={() => { setSelectedItem(item); setShowQRDialog(true); }}
                            title="View QR Code"
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                          
                          {/* Everyone can checkout/checkin */}
                          {item.checked_out ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`checkin-btn-${item.id}`}
                              onClick={() => handleCheckin(item)}
                              className="text-emerald-600 hover:text-emerald-700"
                              title="Check In"
                            >
                              <ArrowDownRight className="w-4 h-4" />
                            </Button>
                          ) : canCheckout(item) ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`checkout-btn-${item.id}`}
                              onClick={() => handleCheckout(item)}
                              className="text-amber-600 hover:text-amber-700"
                              title="Check Out"
                            >
                              <ArrowUpRight className="w-4 h-4" />
                            </Button>
                          ) : null}
                          
                          {isManager() && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`edit-btn-${item.id}`}
                                onClick={() => handleOpenDialog(item)}
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-btn-${item.id}`}
                                onClick={() => { setSelectedItem(item); setShowDeleteDialog(true); }}
                                className="text-destructive hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>
              {selectedItem ? 'Update the item details below.' : 'Fill in the details for the new item.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="item-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Sony Camera HDR-CX405"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="item_number">Item Number *</Label>
                <Input
                  id="item_number"
                  data-testid="item-number-input"
                  value={formData.item_number}
                  onChange={(e) => setFormData({ ...formData, item_number: e.target.value.toUpperCase() })}
                  placeholder="e.g., CAM-001"
                  className="font-mono"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="quantity">Quantity *</Label>
                <Input
                  id="quantity"
                  data-testid="item-quantity-input"
                  type="number"
                  min="1"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Category *</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                  <SelectTrigger data-testid="item-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Storage Location *</Label>
                <Select value={formData.storage_location_id} onValueChange={(v) => setFormData({ ...formData, storage_location_id: v })}>
                  <SelectTrigger data-testid="item-location-select">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="length">Length (if applicable)</Label>
                <Input
                  id="length"
                  data-testid="item-length-input"
                  value={formData.length}
                  onChange={(e) => setFormData({ ...formData, length: e.target.value })}
                  placeholder="e.g., 25ft, 10m"
                />
              </div>
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger data-testid="item-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.filter(s => s.value !== 'checked_out').map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                data-testid="item-notes-input"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this item..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button 
              data-testid="save-item-btn"
              onClick={handleSave}
              disabled={!formData.name || !formData.item_number || !formData.category_id || !formData.storage_location_id}
            >
              {selectedItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>
              Scan to view item details or print for physical label
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG 
                  value={`${window.location.origin}/item/${selectedItem.id}`}
                  size={200}
                  level="H"
                />
              </div>
              <div className="text-center">
                <p className="font-semibold">{selectedItem.name}</p>
                <code className="text-sm text-muted-foreground">{selectedItem.item_number}</code>
              </div>
              <Button onClick={() => handlePrintQR(selectedItem)} className="w-full" data-testid="print-qr-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Batch Print Dialog */}
      <Dialog open={showBatchPrintDialog} onOpenChange={setShowBatchPrintDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Batch Print QR Codes</DialogTitle>
            <DialogDescription>
              Print QR code labels for {selectedItems.size} selected item{selectedItems.size !== 1 ? 's' : ''}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {items.filter(item => selectedItems.has(item.id)).map(item => (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <code className="text-xs text-muted-foreground">{item.item_number}</code>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleSelectItem(item.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBatchPrintDialog(false)}>Cancel</Button>
            <Button onClick={handleBatchPrint} data-testid="confirm-batch-print-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print All ({selectedItems.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedItem?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-btn" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Detect Dialog */}
      <AIDetectDialog
        open={showAIDetect}
        onClose={() => setShowAIDetect(false)}
        detectionType="item"
        onDetected={handleAIDetected}
      />
    </div>
  );
};

export default Items;
